import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, X } from "lucide-react";
import "./ConfirmDialog.css";
import { useAuth } from "../context/AuthContext";

const ConfirmDialog = ({
  isOpen,
  title,
  message,
  children,
  onConfirm,
  onCancel,
  confirmText = "Confirm",
  cancelText = "Cancel",
  type = "warning",
  requireConsent = false,
}) => {
  const { isAuthenticated } = useAuth();
  const [hasConsented, setHasConsented] = useState(false);
  const [consentError, setConsentError] = useState(false);
  const isSafari = useMemo(() => {
    if (typeof window === "undefined" || typeof navigator === "undefined") {
      return false;
    }

    const userAgent = navigator.userAgent || "";
    const isWebKit = /WebKit/i.test(userAgent);
    const isSafariBrowser = /Safari/i.test(userAgent);
    const isOtherBrowser = /Chrome|Chromium|CriOS|FxiOS|EdgiOS|OPiOS|OPR|Android/i.test(userAgent);

    return isWebKit && isSafariBrowser && !isOtherBrowser;
  }, []);

  const handleConsentChange = (e) => {
    const checked = e.target.checked;
    setHasConsented(checked);
    if (checked) {
      setConsentError(false);
      localStorage.setItem("termsConsent", "true");
    } else {
      localStorage.removeItem("termsConsent");
    }
  };

  // Reset to unchecked every time dialog opens — require affirmative consent each time
  useEffect(() => {
    if (isOpen) {
      setHasConsented(false);
      setConsentError(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleConfirmClick = () => {
    if (requireConsent && !hasConsented) {
      setConsentError(true);
      return;
    }
    onConfirm();
  };

  // Determine if we should use specialized debate styling
  const isDebate = type === "debate";
  const containerClass = isDebate
    ? "confirm-dialog confirm-dialog-debate"
    : "confirm-dialog";
  const overlayClassName = isSafari
    ? "confirm-dialog-overlay confirm-dialog-overlay-safari"
    : "confirm-dialog-overlay";
  const dialogClassName = isSafari
    ? `${containerClass} confirm-dialog-safari`
    : containerClass;
  const overlayMotionProps = isSafari
    ? { initial: false, animate: { opacity: 1 }, exit: { opacity: 0 } }
    : {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
    };
  const dialogMotionProps = isSafari
    ? { initial: false, animate: { opacity: 1 }, exit: { opacity: 0 } }
    : {
      initial: { opacity: 0, scale: 0.95, y: "-40%" },
      animate: { opacity: 1, scale: 1, y: "-50%" },
      exit: { opacity: 0, scale: 0.95, y: "-40%" },
    };

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className={overlayClassName}
            initial={overlayMotionProps.initial}
            animate={overlayMotionProps.animate}
            exit={overlayMotionProps.exit}
            onClick={onCancel}
          />
          <motion.div
            className={dialogClassName}
            initial={dialogMotionProps.initial}
            animate={dialogMotionProps.animate}
            exit={dialogMotionProps.exit}
            transition={{ duration: 0.2, ease: "easeOut" }}
            role="dialog"
            aria-modal="true"
          >
            <div
              className={`confirm-dialog-header ${isDebate ? "confirm-dialog-header-debate" : ""}`}
            >
              {!isDebate && (
                <div
                  className={`confirm-dialog-icon-wrapper ${type === "danger" ? "danger" : ""}`}
                >
                  <AlertTriangle
                    size={24}
                    className={`confirm-dialog-icon confirm-dialog-icon-${type}`}
                  />
                </div>
              )}
              {isDebate && (
                <div className="confirm-dialog-title-wrapper">
                  <h3 className="confirm-dialog-title">{title}</h3>
                </div>
              )}
              <button
                className="confirm-dialog-close"
                onClick={onCancel}
                aria-label="Close dialog"
              >
                <X size={20} />
              </button>
            </div>

            <div className="confirm-dialog-content">
              {!isDebate && <h3 className="confirm-dialog-title">{title}</h3>}

              {/* If message is a string, wrap in p, otherwise render as is (for custom JSX) */}
              {message &&
                (typeof message === "string" ? (
                  <p className="confirm-dialog-message">{message}</p>
                ) : (
                  <div className="confirm-dialog-custom-content">{message}</div>
                ))}

              {children && (
                <div className="confirm-dialog-custom-content">{children}</div>
              )}
            </div>

            <div className="confirm-dialog-actions">
              {requireConsent && (
                <div className="confirm-dialog-consent">
                  <label>
                    <input
                      type="checkbox"
                      checked={hasConsented}
                      onChange={handleConsentChange}
                    />
                    <span>
                      I agree to the{" "}
                      <a
                        href="/terms-of-service"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Terms
                      </a>{" "}
                      &{" "}
                      <a
                        href="/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Privacy Policy
                      </a>
                    </span>
                  </label>
                  {consentError && (
                    <p className="confirm-dialog-consent-error">
                      Please agree to the Terms & Privacy Policy.
                    </p>
                  )}
                </div>
              )}
              <button
                className="confirm-dialog-button confirm-dialog-button-cancel"
                onClick={onCancel}
              >
                {cancelText}
              </button>
              <button
                className={`confirm-dialog-button confirm-dialog-button-${type}`}
                onClick={handleConfirmClick}
              >
                {confirmText}
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default ConfirmDialog;
